import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useSpacePlugins, usePluginEventBus } from '../../hooks/usePlugins';
import {
  useOrgPluginCatalog,
  useOrgPluginCatalogSources,
} from '../../hooks/useOrgPluginCatalog';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Button } from '@/components/ui';
import InstallPluginModal from './InstallPluginModal';
import CatalogPluginsList from './CatalogPluginsList';
import InstalledPluginsList from './InstalledPluginsList';

interface PluginListProps {
  /** Set by the /plugins/new route to auto-open the install modal on mount. */
  openInstallOnMount?: boolean;
}

export default function PluginList({ openInstallOnMount = false }: PluginListProps) {
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { data, isLoading } = useSpacePlugins();
  const { data: catalog } = useOrgPluginCatalog();
  const { data: catalogSources } = useOrgPluginCatalogSources();

  // Live invalidation + completion toasts.
  usePluginEventBus();

  const [installOpen, setInstallOpen] = useState(openInstallOnMount);

  useEffect(() => {
    if (openInstallOnMount) setInstallOpen(true);
  }, [openInstallOnMount]);

  const plugins = data?.items || [];
  const catalogEntries = catalog?.plugins ?? [];
  const hasCatalog = catalogEntries.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Plugins</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Install plugins from a git repository
          </p>
        </div>
        <Button onClick={() => setInstallOpen(true)}>Install plugin</Button>
      </div>

      {hasCatalog && (
        <CatalogPluginsList
          entries={catalogEntries}
          installed={plugins}
          sources={catalogSources?.sources ?? []}
        />
      )}

      {plugins.length === 0 && !isLoading && !hasCatalog ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <p className="text-foreground font-medium">No plugins installed yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Install a plugin from any public git repository.
          </p>
          <div className="mt-4 flex items-center justify-center">
            <Link
              to="https://code.claude.com/docs/en/discover-plugins"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              Learn more
              <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
      ) : (
        <section className="space-y-3">
          {hasCatalog && (
            <h2 className="text-sm text-muted-foreground">Installed</h2>
          )}
          <InstalledPluginsList plugins={plugins} isLoading={isLoading} />
        </section>
      )}

      <InstallPluginModal
        isOpen={installOpen}
        onClose={() => {
          setInstallOpen(false);
          // If the user landed via /plugins/new, drop the modal-open URL so
          // navigating back doesn't reopen it.
          if (openInstallOnMount) navigate(routes.console.spacePlugins(spaceId), { replace: true });
        }}
      />
    </div>
  );
}
